/** The facilities module's reads and writes of the API: TanStack Query hooks, and a few plain calls for a form to make as it is filled in. */
import { useMemo } from 'react'
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiGetPage, apiPatch, apiPost, apiPut, isNotFoundError } from '../core/index.js'
import { DEFAULT_FACILITY_CONFIG } from './defaultFacilityConfig.js'
import { schedulingRulesOf } from './defaultSchedulingRules.js'
import type { CreateFacilityInput, DashboardSummary, Facility, FacilityAddress, FacilityConfig, FacilitySummary, HolidayEvent, TierDistribution, TimezoneSuggestion, UpdateFacilityInput } from './types.js'

/** How many facilities a list shows to begin with, and how many each "Show more" adds: a few first, so the page opens short, then enough that reaching further down takes few clicks. */
export const FACILITY_PAGE_SIZES = { first: 5, more: 25 } as const

/** Long enough for a new facility to reach the index the lists are read from. */
const FACILITY_INDEX_SETTLE_MS = 2000

/**
 * What the shell needs before it can draw anything: how many facilities
 * this person can see and, when it is exactly one, which. One row and the
 * list's total, instead of the whole list - a client may have hundreds.
 * Every facility query's key starts with 'facilities', so one invalidation
 * of that refreshes them all.
 */
export function useFacilityOverview() {
  return useQuery({
    queryKey: ['facilities', 'overview'],
    queryFn: async () => {
      const page = await apiGetPage<FacilitySummary>('/facilities?limit=1', null)
      // An API from before paging answers the whole list with no total: its length is the total.
      return { total: page.total ?? page.items.length, first: page.items[0] ?? null }
    },
  })
}

/**
 * The facilities this person can see, in name order, loaded a page at a
 * time; `search` narrows them to names starting with it. The previous
 * results stay on screen while a new search loads.
 */
export function useFacilityPages(search: string, sizes: { first: number; more: number } = FACILITY_PAGE_SIZES) {
  const q = search.trim()
  return useInfiniteQuery({
    queryKey: ['facilities', 'pages', q, sizes.first, sizes.more],
    queryFn: ({ pageParam }) => apiGetPage<FacilitySummary>(`/facilities?limit=${pageParam === null ? sizes.first : sizes.more}${q ? `&q=${encodeURIComponent(q)}` : ''}`, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    placeholderData: keepPreviousData,
  })
}

/** One facility, by id: what a screen showing a facility asks for, rather than finding it in the list. A 404 is an answer, not something to retry. */
export function useFacility(facilityId: string | null | undefined) {
  return useQuery({
    queryKey: ['facilities', 'one', facilityId],
    queryFn: () => apiGet<FacilitySummary>(`/facilities/${encodeURIComponent(facilityId as string)}`),
    enabled: Boolean(facilityId),
    retry: (failureCount, error) => !isNotFoundError(error) && failureCount < 2,
  })
}

export function useDashboardSummary(facilityId: string) {
  return useQuery({
    queryKey: ['dashboard-summary', facilityId],
    queryFn: () => apiGet<DashboardSummary>(`/facilities/${facilityId}/dashboard/summary`),
  })
}

export function useTierDistribution(facilityId: string) {
  return useQuery({
    queryKey: ['tier-distribution', facilityId],
    queryFn: () => apiGet<TierDistribution>(`/facilities/${facilityId}/dashboard/tier-distribution`),
  })
}

export function useHolidayEvents(facilityId: string | null) {
  return useQuery({
    queryKey: ['holiday-events', facilityId],
    queryFn: () => apiGet<HolidayEvent[]>(`/facilities/${encodeURIComponent(facilityId as string)}/holiday-events`),
    enabled: facilityId !== null,
  })
}

/**
 * A facility's units by id, for text that names a unit (26 October 2026: a sentence never shows a unit's id, as
 * "Census rose on unit-b" did). From a configuration already read, for the admin's screens that read it their own way.
 */
export function unitNamesFromConfig(config: Pick<FacilityConfig, 'scheduling'> | null | undefined): ReadonlyMap<string, string> {
  return new Map(schedulingRulesOf(config ?? null).units.map((unit) => [unit.id, unit.name]))
}

/** The same for a staff screen, from the facility's configuration; empty until it is read, so text falls back to the id. */
export function useUnitNames(facilityId: string | null): ReadonlyMap<string, string> {
  const config = useFacilityConfig(facilityId).data
  return useMemo(() => unitNamesFromConfig(config), [config])
}

/** A facility's configuration; idle while `facilityId` is null, for a caller that learns the facility from another query. */
export function useFacilityConfig(facilityId: string | null) {
  return useQuery({
    queryKey: ['facility-config', facilityId],
    queryFn: () => apiGet<FacilityConfig>(`/facilities/${facilityId as string}/config`),
    enabled: facilityId !== null,
  })
}

export function useUpdateFacilityConfig(facilityId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (config: FacilityConfig) => apiPut<FacilityConfig>(`/facilities/${facilityId}/config`, config),
    onSuccess: (updated) => {
      queryClient.setQueryData(['facility-config', facilityId], updated)
    },
  })
}

/**
 * An operator registering a facility of its own: register it, record its
 * address and phone when given (a separate call, because registration takes
 * neither - the platform admin can call that path, and only an operator may
 * set them), then give it the default configuration so its dashboard, roster
 * and configuration page open at once rather than 404 until someone sets it
 * up. The operator adjusts the configuration from there. Refreshes every
 * facility list afterwards.
 */
export function useCreateFacility(clientId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ address, phone, ...input }: CreateFacilityInput & { address?: FacilityAddress; phone?: string }) => {
      const created = await apiPost<Facility>(`/admin/clients/${encodeURIComponent(clientId ?? '')}/facilities`, input)
      const path = `/facilities/${encodeURIComponent(created.facilityId)}`
      const contact: UpdateFacilityInput = { ...(address ? { address } : {}), ...(phone ? { phone } : {}) }
      const withContact = Object.keys(contact).length > 0 ? await apiPatch<Facility>(path, contact) : created
      await apiPut<FacilityConfig>(`${path}/config`, { facilityId: created.facilityId, ...DEFAULT_FACILITY_CONFIG })
      return withContact
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['facilities'] })
      // The facility lists are read from an index that can trail a write by a moment, and the shell decides its
      // whole layout from the count: ask once more shortly after, so a first facility is never left looking absent.
      setTimeout(() => void queryClient.invalidateQueries({ queryKey: ['facilities'] }), FACILITY_INDEX_SETTLE_MS)
    },
  })
}

/** An operator changing its own facility's name, time zone or address. Refreshes every facility list afterwards. */
export function useUpdateFacility(facilityId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateFacilityInput) => apiPatch<Facility>(`/facilities/${encodeURIComponent(facilityId)}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['facilities'] })
    },
  })
}

/** The zone a postal code falls in, for a person to confirm. Not a hook: the form asks as the postal code is typed. */
export function fetchTimezoneSuggestion(postalCode: string, country: string): Promise<TimezoneSuggestion> {
  const params = new URLSearchParams({ postalCode, country })
  return apiGet<TimezoneSuggestion>(`/timezones/suggestion?${params.toString()}`)
}
